import { ApplicationRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationResult,
  Configuration,
  IPublicClientApplication,
  PopupRequest,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';
import { CustomerIdentityTenantConfig, ENVIRONMENT } from '@ld.consumer-portal/models';
import { STEP_UP_MFA_TOKEN_KEY } from '../interceptors/auth.interceptor';
import { AuthService } from './auth.service';
import { CustomerIdentityService } from './customer-identity.service';
import { EntraLoginAccelerationEvaluatorService } from './entra-login-acceleration-evaluator.service';
import { LoggingService } from './logging.service';
import { NativeCiamAuthService } from './native-ciam-auth.service';
import { PlatformService } from './platform.service';

const CONSUMER_PORTAL_API_SCOPES = ['api://consumerportal/user_impersonation'];
const CIAM_CALLBACK_PATH = '/ciam/callback';
const REDIRECT_RESPONSE_KEYS = ['code', 'error', 'state'];

export interface CiamCallbackResult {
  accessToken: string;
  idToken: string;
  expiresOn: Date | null;
  correlationId?: string;
  account: {
    homeAccountId: string;
    localAccountId: string;
    name?: string;
    username: string;
  } | null;
}

@Injectable({
  providedIn: 'root',
})
export class CiamAuthService {
  private readonly router = inject(Router);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(AuthService);
  private readonly customerIdentityService = inject(CustomerIdentityService);
  private readonly platformService = inject(PlatformService);
  private readonly zone = inject(NgZone);
  private readonly appRef = inject(ApplicationRef);
  private readonly nativeCiamAuthService = inject(NativeCiamAuthService);
  private readonly entraLoginAccelerationService = inject(EntraLoginAccelerationEvaluatorService);
  private readonly loggingService = inject(LoggingService);
  private msalInstance: IPublicClientApplication | null = null;
  private initialized = false;
  private mockMode = false;
  private readonly accountStateVersion = signal(0);

  setMockMode(enabled: boolean): void {
    this.mockMode = enabled;
  }

  private get isMockMode(): boolean {
    return this.mockMode;
  }

  async initialize(tenantConfig: CustomerIdentityTenantConfig): Promise<void> {
    if (this.isMockMode) {
      this.initialized = true;
      return;
    }

    const ciamDomain = tenantConfig.ciamDomain;
    if (!ciamDomain || ciamDomain.trim() === '') {
      throw new Error('CIAM domain is not available from TenantConfig. Ensure ciamDomain is returned by the TenantConfiguration API.');
    }

    const fullCiamDomain = `${ciamDomain}.ciamlogin.com`;
    const msalConfig: Configuration = {
      auth: {
        clientId: tenantConfig.applicationId,
        authority: `https://${fullCiamDomain}/${tenantConfig.entraTenantID}`,
        redirectUri: `${window.location.origin}/ciam/callback`,
        postLogoutRedirectUri: `${window.location.origin}/login`,
        knownAuthorities: [fullCiamDomain],
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
    this.initialized = true;
    this.accountStateVersion.update((version) => version + 1);
  }

  isInitialized(): boolean {
    return this.initialized && (this.isMockMode || this.msalInstance !== null);
  }

  getExternalUserId(): string | null {
    this.accountStateVersion();

    if (this.isMockMode) {
      return 'mock-ciam-user-id-12345';
    }

    if (!this.msalInstance) {
      return this.authService.getExternalUserId();
    }

    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0].localAccountId : this.authService.getExternalUserId();
  }

  getEntraTenantId(): string {
    const storedConfig = sessionStorage.getItem('ciam_tenant_config');
    if (!storedConfig) {
      return '';
    }

    try {
      const config = JSON.parse(storedConfig) as CustomerIdentityTenantConfig;
      return config.entraTenantID ?? '';
    } catch {
      return '';
    }
  }

  async loginRedirect(email: string): Promise<void> {
    if (this.isMockMode) {
      sessionStorage.setItem('ciam_mock_email', email);
      await this.router.navigate(['/ciam/callback'], { queryParams: { mock: 'true' } });
      return;
    }

    if (!this.msalInstance) {
      throw new Error('CiamAuthService not initialized. Call initialize() first.');
    }

    await this.msalInstance.handleRedirectPromise();

    // Last step before authorize: blocklisted domains resolve to their own Entra home tenant
    // when a login_hint is sent, which drops the user on that tenant's password step. Resolving
    // here also holds the email re-entry notice on screen until the redirect actually fires.
    // See EntraLoginAccelerationEvaluatorService.
    const loginHint = await this.entraLoginAccelerationService.resolveLoginHint(email);

    const loginRequest: RedirectRequest = {
      scopes: ['api://consumerportal/user_impersonation'],
      prompt: 'login',
      ...(loginHint ? { loginHint } : {}),
    };

    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      // On success the browser is already navigating, so the notice stays up until the page
      // unloads. Only a failed hand-off has to give the login form back to the borrower.
      this.entraLoginAccelerationService.hideEmailReentryNotice();
      throw error;
    }
  }

  async handleCallback(): Promise<CiamCallbackResult | null> {
    if (this.isMockMode) {
      const email = sessionStorage.getItem('ciam_mock_email') || 'mockuser@example.com';
      sessionStorage.removeItem('ciam_mock_email');
      return this.buildMockCallbackResult(email);
    }

    if (!this.msalInstance) {
      throw new Error('CiamAuthService not initialized. Call initialize() first.');
    }

    const locationHash = window.location.hash;
    const navigationEntry = this.getNavigationEntry();
    const navigationHash = this.getNavigationEntryCallbackHash(navigationEntry);
    // iOS Safari can expose the callback document without its fragment long enough for the
    // Router's initial navigation to replaceState it away before MSAL reads it. The navigation
    // timing entry keeps the original URL, so hand MSAL that fragment instead. MSAL still
    // validates state and PKCE against its cached request.
    const fallbackHash = !this.hasRedirectResponse(locationHash) && navigationHash ? navigationHash : undefined;

    if (fallbackHash) {
      this.loggingService.logInfo('[CiamAuthService] Using navigation entry fragment for redirect response', {
        locationHashKeys: this.fragmentKeys(locationHash),
        navigationEntryHashKeys: this.fragmentKeys(fallbackHash),
      });
    }

    const response: AuthenticationResult | null = await this.msalInstance.handleRedirectPromise(fallbackHash);

    this.accountStateVersion.update((version) => version + 1);

    if (!response) {
      if (this.hasRedirectResponse(locationHash) || navigationHash) {
        this.loggingService.logWarning('[CiamAuthService] Redirect response present but MSAL returned no result', {
          locationHashKeys: this.fragmentKeys(locationHash),
          navigationEntryHashKeys: this.fragmentKeys(navigationHash ?? ''),
          navigationType: navigationEntry?.type,
          usedNavigationEntryFallback: !!fallbackHash,
          hasInteractionStatus: sessionStorage.getItem('msal.interaction.status') !== null,
          userAgent: navigator.userAgent,
        });
      }
      return null;
    }

    return {
      accessToken: response.accessToken,
      idToken: response.idToken,
      expiresOn: response.expiresOn,
      correlationId: response.correlationId,
      account: response.account
        ? {
            homeAccountId: response.account.homeAccountId,
            localAccountId: response.account.localAccountId,
            name: response.account.name ?? undefined,
            username: response.account.username,
          }
        : null,
    };
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.msalInstance) {
      return null;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes: ['api://consumerportal/user_impersonation'],
        account: accounts[0],
      });
      return result.accessToken;
    } catch {
      return null;
    }
  }

  async refreshToken(): Promise<{ accessToken: string; idToken: string; expiresOn: Date | null } | null> {
    if (this.isMockMode || !this.msalInstance) {
      return null;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes: ['api://consumerportal/user_impersonation'],
        account: accounts[0],
        forceRefresh: true,
      });
      return {
        accessToken: result.accessToken,
        idToken: result.idToken,
        expiresOn: result.expiresOn,
      };
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    if (!this.msalInstance) {
      return;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      await this.msalInstance.logoutRedirect({
        account: accounts[0],
      });
    }
  }

  reset(): void {
    this.msalInstance = null;
    this.initialized = false;
    this.accountStateVersion.update((version) => version + 1);
  }

  async ensureInitialized(): Promise<void> {
    if (this.isInitialized()) {
      return;
    }

    this.setMockMode(!this.env.production && !!this.env.mockConfig?.enabled);

    const storedConfig = sessionStorage.getItem('ciam_tenant_config');
    if (storedConfig) {
      try {
        const tenantConfig = JSON.parse(storedConfig) as CustomerIdentityTenantConfig;
        await this.initialize(tenantConfig);
        return;
      } catch {
        sessionStorage.removeItem('ciam_tenant_config');
      }
    }

    const host =
      window.location.hostname === 'localhost'
        ? (this.env.appSettings.ciamTenantHost ?? 'consumerportal-dv1.loandepotdev.works')
        : window.location.hostname;
    const tenantConfig = await this.customerIdentityService.getTenantConfig(host);
    await this.initialize(tenantConfig);
    sessionStorage.setItem(
      'ciam_tenant_config',
      JSON.stringify({
        applicationId: tenantConfig.applicationId,
        entraTenantID: tenantConfig.entraTenantID,
        tenantHost: tenantConfig.tenantHost,
        tenantName: tenantConfig.tenantName,
        isMFAEnabled: tenantConfig.isMFAEnabled,
        ciamDomain: tenantConfig.ciamDomain,
      }),
    );
  }

  async redirectForPasswordChange(): Promise<void> {
    // eslint-disable-next-line no-console
    console.log('[ChangePassword] redirectForPasswordChange called: mockMode=' + this.isMockMode +
      ', initialized=' + this.initialized + ', msalInstance=' + (this.msalInstance ? 'present' : 'null'));

    if (this.isMockMode) {
      // eslint-disable-next-line no-console
      console.log('[ChangePassword] Mock mode: setting password_change_return_url and navigating to /ciam/callback');
      sessionStorage.setItem('password_change_return_url', '/dashboard');
      sessionStorage.setItem('ciam_mock_email', 'mockuser@example.com');
      await this.router.navigate(['/ciam/callback'], { queryParams: { mock: 'true' } });
      return;
    }

    await this.ensureInitialized();

    if (!this.msalInstance) {
      // eslint-disable-next-line no-console
      console.error('[ChangePassword] MSAL not initialized — cannot redirect for password change');
      throw new Error('CiamAuthService not initialized. Call initialize() first.');
    }

    const accounts = this.msalInstance.getAllAccounts();
    // eslint-disable-next-line no-console
    console.log('[ChangePassword] MSAL accounts count=' + accounts.length);
    if (accounts.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[ChangePassword] account[0]: username=' + accounts[0].username +
        ', homeAccountId=' + accounts[0].homeAccountId +
        ', tenantId=' + accounts[0].tenantId +
        ', environment=' + accounts[0].environment);
    }
    if (accounts.length === 0) {
      // eslint-disable-next-line no-console
      console.error('[ChangePassword] No authenticated accounts in MSAL — cannot redirect');
      throw new Error('No authenticated user. User must log in first.');
    }

    sessionStorage.setItem('password_change_return_url', '/dashboard');

    const msalConfig = this.msalInstance.getConfiguration();
    // eslint-disable-next-line no-console
    console.log('[ChangePassword] MSAL config: authority=' + msalConfig.auth.authority +
      ', redirectUri=' + msalConfig.auth.redirectUri +
      ', knownAuthorities=' + JSON.stringify(msalConfig.auth.knownAuthorities));

    try {
      // eslint-disable-next-line no-console
      console.log('[ChangePassword] Calling handleRedirectPromise to flush pending state');
      await this.msalInstance.handleRedirectPromise();

      // eslint-disable-next-line no-console
      console.log('[ChangePassword] Calling acquireTokenRedirect with prompt=login, account=' + accounts[0].username);
      await this.msalInstance.acquireTokenRedirect({
        scopes: ['api://consumerportal/user_impersonation'],
        account: accounts[0],
        prompt: 'login',
      });
      // eslint-disable-next-line no-console
      console.log('[ChangePassword] acquireTokenRedirect completed (browser should redirect now)');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ChangePassword] acquireTokenRedirect FAILED:', error instanceof Error ? error.message : String(error));
      sessionStorage.removeItem('password_change_return_url');
      throw error;
    }
  }

  async requestStepUpMfa(authContextId: string): Promise<CiamCallbackResult | null> {
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] requestStepUpMfa called: authContextId=' + authContextId +
      ', mockMode=' + this.isMockMode + ', initialized=' + this.initialized +
      ', msalInstance=' + (this.msalInstance ? 'present' : 'null'));

    if (this.isMockMode) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: mock mode active, returning mock result');
      return this.buildMockCallbackResult('mockuser@example.com');
    }

    if (this.platformService.isNativeMobile()) {
      return this.requestNativeStepUpMfa(authContextId);
    }

    if (!this.msalInstance) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: MSAL not initialized, throwing error');
      throw new Error('CiamAuthService not initialized. Call initialize() first.');
    }

    const msalConfig = this.msalInstance.getConfiguration();
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] MSAL config: authority=' + msalConfig.auth.authority +
      ', redirectUri=' + msalConfig.auth.redirectUri +
      ', knownAuthorities=' + JSON.stringify(msalConfig.auth.knownAuthorities));

    const accounts = this.msalInstance.getAllAccounts();
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] requestStepUpMfa: accounts count=' + accounts.length);
    if (accounts.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] account[0]: username=' + accounts[0].username +
        ', homeAccountId=' + accounts[0].homeAccountId +
        ', tenantId=' + accounts[0].tenantId +
        ', environment=' + accounts[0].environment);
    }
    if (accounts.length === 0) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: no authenticated accounts, throwing error');
      throw new Error('No authenticated user. User must log in first.');
    }

    const claimsRequest = JSON.stringify({
      access_token: {
        acrs: {
          essential: true,
          value: authContextId,
        },
      },
    });

    try {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: calling handleRedirectPromise to flush pending state');
      await this.msalInstance.handleRedirectPromise();

      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: calling acquireTokenPopup with claims=' +
        claimsRequest + ', account=' + accounts[0].username);
      const result = await this.msalInstance.acquireTokenPopup({
        scopes: CONSUMER_PORTAL_API_SCOPES,
        account: accounts[0],
        claims: claimsRequest,
      } as PopupRequest);
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfa: acquireTokenPopup completed');

      this.persistStepUpTokens(result);

      return {
        accessToken: result.accessToken,
        idToken: result.idToken,
        expiresOn: result.expiresOn,
        account: result.account
          ? {
              homeAccountId: result.account.homeAccountId,
              localAccountId: result.account.localAccountId,
              name: result.account.name ?? undefined,
              username: result.account.username,
            }
          : null,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[StepUpMFA] requestStepUpMfa: acquireTokenPopup FAILED: ' +
        (error instanceof Error ? error.message : String(error)));
      throw error;
    }
  }

  private persistStepUpTokens(result: AuthenticationResult | CiamCallbackResult): void {
    if (result.accessToken) {
      sessionStorage.setItem(STEP_UP_MFA_TOKEN_KEY, result.accessToken);
    }
  }

  /**
   * Native step-up MFA: MSAL popups and redirects are unavailable inside Capacitor,
   * so the challenge runs through the system browser and resolves inline.
   */
  private async requestNativeStepUpMfa(authContextId: string): Promise<CiamCallbackResult> {
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] requestStepUpMfa: native mobile detected, delegating to NativeCiamAuthService.stepUp');

    const email = this.getCurrentUsername();

    try {
      const result = await this.nativeCiamAuthService.stepUp(authContextId, email);
      this.persistStepUpTokens(result);
      return result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[StepUpMFA] requestStepUpMfa: native step-up FAILED: ' +
        (error instanceof Error ? error.message : String(error)));
      throw error;
    } finally {
      this.scheduleChangeDetection();
    }
  }

  /**
   * The generic-oauth2 promise settles from the native bridge, outside the Angular
   * zone, so state that callers set after awaiting step-up (e.g. advancing the MFA
   * preferences modal to its form step) is marked dirty but never rendered. Queuing a
   * task inside the zone runs change detection once those continuations have finished.
   */
  private scheduleChangeDetection(): void {
    this.zone.run(() => {
      setTimeout(() => this.appRef.tick());
    });
  }

  private getCurrentUsername(): string | undefined {
    const accounts = this.msalInstance?.getAllAccounts() ?? [];
    return accounts.length > 0 ? accounts[0].username : undefined;
  }

  private getNavigationEntry(): PerformanceNavigationTiming | undefined {
    if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
      return undefined;
    }
    return performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  }

  /**
   * Returns the redirect-response fragment the current document was loaded with, but only for a
   * fresh navigation to the CIAM callback route. Reloads and history traversals are excluded so a
   * consumed authorization code is never replayed.
   */
  private getNavigationEntryCallbackHash(entry: PerformanceNavigationTiming | undefined): string | undefined {
    if (window.location.pathname !== CIAM_CALLBACK_PATH || entry?.type !== 'navigate') {
      return undefined;
    }
    try {
      const url = new URL(entry.name);
      return url.pathname === CIAM_CALLBACK_PATH && this.hasRedirectResponse(url.hash) ? url.hash : undefined;
    } catch {
      return undefined;
    }
  }

  private hasRedirectResponse(hash: string): boolean {
    const keys = this.fragmentKeys(hash).split(',');
    return REDIRECT_RESPONSE_KEYS.some((key) => keys.includes(key));
  }

  private fragmentKeys(hash: string): string {
    const keys: string[] = [];
    new URLSearchParams(hash.replace(/^#/, '')).forEach((_value, key) => keys.push(key));
    return keys.join(',');
  }

  async requestStepUpMfaRedirect(authContextId: string, returnUrl: string): Promise<void> {
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] requestStepUpMfaRedirect called: authContextId=' + authContextId +
      ', returnUrl=' + returnUrl + ', mockMode=' + this.isMockMode +
      ', initialized=' + this.initialized + ', msalInstance=' + (this.msalInstance ? 'present' : 'null'));

    if (this.isMockMode) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfaRedirect: mock mode — storing step-up state and navigating to /ciam/callback');
      sessionStorage.setItem('step_up_return_url', returnUrl);
      sessionStorage.setItem('step_up_context_id', authContextId);
      sessionStorage.setItem('ciam_mock_email', 'mockuser@example.com');
      await this.router.navigate(['/ciam/callback'], { queryParams: { mock: 'true' } });
      return;
    }

    await this.ensureInitialized();

    if (!this.msalInstance) {
      // eslint-disable-next-line no-console
      console.error('[StepUpMFA] requestStepUpMfaRedirect: MSAL not initialized — cannot redirect');
      throw new Error('CiamAuthService not initialized. Call initialize() first.');
    }

    const accounts = this.msalInstance.getAllAccounts();
    // eslint-disable-next-line no-console
    console.log('[StepUpMFA] requestStepUpMfaRedirect: accounts count=' + accounts.length);
    if (accounts.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfaRedirect account[0]: username=' + accounts[0].username +
        ', homeAccountId=' + accounts[0].homeAccountId);
    }
    if (accounts.length === 0) {
      // eslint-disable-next-line no-console
      console.error('[StepUpMFA] requestStepUpMfaRedirect: no authenticated accounts — cannot redirect');
      throw new Error('No authenticated user. User must log in first.');
    }

    const claimsRequest = JSON.stringify({
      access_token: {
        acrs: {
          essential: true,
          value: authContextId,
        },
      },
    });

    sessionStorage.setItem('step_up_return_url', returnUrl);
    sessionStorage.setItem('step_up_context_id', authContextId);

    try {
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfaRedirect: calling handleRedirectPromise to flush pending state');
      await this.msalInstance.handleRedirectPromise();

      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfaRedirect: calling acquireTokenRedirect with claims=' +
        claimsRequest + ', account=' + accounts[0].username);
      await this.msalInstance.acquireTokenRedirect({
        scopes: CONSUMER_PORTAL_API_SCOPES,
        account: accounts[0],
        claims: claimsRequest,
      });
      // eslint-disable-next-line no-console
      console.log('[StepUpMFA] requestStepUpMfaRedirect: acquireTokenRedirect completed (browser should redirect now)');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[StepUpMFA] requestStepUpMfaRedirect FAILED:', error instanceof Error ? error.message : String(error));
      sessionStorage.removeItem('step_up_return_url');
      sessionStorage.removeItem('step_up_context_id');
      throw error;
    }
  }

  clearStepUpState(): void {
    sessionStorage.removeItem('step_up_verified');
    sessionStorage.removeItem('step_up_verified_at');
    sessionStorage.removeItem('step_up_return_url');
    sessionStorage.removeItem('step_up_context_id');
    sessionStorage.removeItem('step_up_pending_save_index');
    sessionStorage.removeItem('step_up_pending_form_data');
    sessionStorage.removeItem('step_up_pending_link_loan');
  }

  validateStepUpClaim(idToken: string, requiredContextId: string): boolean {
    try {
      const payload = idToken.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      const acrs: unknown = decoded.acrs;

      if (Array.isArray(acrs)) {
        return acrs.includes(requiredContextId);
      }
      if (typeof acrs === 'string') {
        return acrs === requiredContextId;
      }
      return false;
    } catch {
      return false;
    }
  }

  private buildMockCallbackResult(email: string): CiamCallbackResult {
    const now = Math.floor(Date.now() / 1000);
    const header = { typ: 'JWT', alg: 'RS256', kid: 'mock-ciam-key' };
    const payload = {
      iss: 'https://mock-ciam.ciamlogin.com/mock-tenant/v2.0/',
      sub: 'mock-ciam-user-id-12345',
      aud: 'mock-ciam-client-id',
      exp: now + 3600,
      iat: now,
      nbf: now,
      oid: 'mock-ciam-user-id-12345',
      given_name: 'Mock',
      family_name: 'User',
      emails: [email],
      preferred_username: email,
    };

    const base64UrlEncode = (obj: object): string => {
      const json = JSON.stringify(obj);
      const base64 = btoa(json);
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    };

    const mockToken = `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.mock_ciam_signature`;

    return {
      accessToken: mockToken,
      idToken: mockToken,
      expiresOn: new Date(Date.now() + 3600000),
      account: {
        homeAccountId: 'mock-ciam-home-account-id',
        localAccountId: 'mock-ciam-user-id-12345',
        name: 'Mock User',
        username: email,
      },
    };
  }
}
